import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LoyaltyClient } from '../../../infrastructure/LoyaltyClient';
import type {
  EarnPointsRequest,
  RedeemPointsRequest,
  UpdateTierRequest,
  CreateTierDefinitionRequest,
  UpdateTierDefinitionRequest,
  UpdateGlobalSettingsRequest,
} from '../../../infrastructure/LoyaltyClient';

export const useLoyalty = () => {
  const queryClient = useQueryClient();
  const loyaltyClient = new LoyaltyClient();
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const customerBalanceQuery = useQuery({
    queryKey: ['customerBalance', selectedCustomerId],
    queryFn: async () => {
      if (!selectedCustomerId) {
        return null;
      }
      return await loyaltyClient.getCustomerBalance(selectedCustomerId);
    },
    enabled: Boolean(selectedCustomerId),
    staleTime: 60 * 1000,
  });

  const transactionHistoryQuery = useQuery({
    queryKey: ['transactionHistory', selectedCustomerId],
    queryFn: async () => {
      if (!selectedCustomerId) {
        return null;
      }
      return await loyaltyClient.getTransactionHistory(selectedCustomerId, 50);
    },
    enabled: Boolean(selectedCustomerId),
    staleTime: 60 * 1000,
  });

  const tierDefinitionsQuery = useQuery({
    queryKey: ['tierDefinitions'],
    queryFn: async () => await loyaltyClient.listTierDefinitions(),
    staleTime: 10 * 60 * 1000,
  });

  const globalSettingsQuery = useQuery({
    queryKey: ['globalLoyaltySettings'],
    queryFn: async () => await loyaltyClient.getGlobalSettings(),
    staleTime: 10 * 60 * 1000,
  });

  const updateGlobalSettingsMutation = useMutation({
    mutationFn: async (request: UpdateGlobalSettingsRequest) => await loyaltyClient.updateGlobalSettings(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['globalLoyaltySettings'] });
    },
    onError: () => {
      setError('Failed to update global settings');
    },
  });

  const earnPointsMutation = useMutation({
    mutationFn: async (request: EarnPointsRequest) => await loyaltyClient.earnPoints(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerBalance', selectedCustomerId] });
      queryClient.invalidateQueries({ queryKey: ['transactionHistory', selectedCustomerId] });
    },
    onError: () => {
      setError('Failed to earn points');
    },
  });

  const redeemPointsMutation = useMutation({
    mutationFn: async (request: RedeemPointsRequest) => await loyaltyClient.redeemPoints(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerBalance', selectedCustomerId] });
      queryClient.invalidateQueries({ queryKey: ['transactionHistory', selectedCustomerId] });
    },
    onError: () => {
      setError('Failed to redeem points');
    },
  });

  const updateTierMutation = useMutation({
    mutationFn: async (request: UpdateTierRequest) => await loyaltyClient.updateCustomerTier(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerBalance', selectedCustomerId] });
    },
    onError: () => {
      setError('Failed to update tier');
    },
  });

  const createTierMutation = useMutation({
    mutationFn: async (request: CreateTierDefinitionRequest) => await loyaltyClient.createTierDefinition(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tierDefinitions'] });
    },
    onError: () => {
      setError('Failed to create tier definition');
    },
  });

  const updateTierDefinitionMutation = useMutation({
    mutationFn: async ({ tierId, request }: { tierId: string; request: UpdateTierDefinitionRequest }) =>
      await loyaltyClient.updateTierDefinition(tierId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tierDefinitions'] });
    },
    onError: () => {
      setError('Failed to update tier definition');
    },
  });

  const deleteTierDefinitionMutation = useMutation({
    mutationFn: async (tierId: string) => await loyaltyClient.deleteTierDefinition(tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tierDefinitions'] });
    },
    onError: () => {
      setError('Failed to delete tier definition');
    },
  });

  const loadMoreTransactions = useCallback(
    async (lastEvaluatedKey: string) => {
      if (!selectedCustomerId) {
        return null;
      }
      return await loyaltyClient.getTransactionHistory(selectedCustomerId, 50, lastEvaluatedKey);
    },
    [loyaltyClient, selectedCustomerId],
  );

  const selectCustomer = useCallback((customerId: string) => {
    setSelectedCustomerId(customerId);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    customerBalance: customerBalanceQuery.data,
    transactionHistory: transactionHistoryQuery.data,
    tierDefinitions: tierDefinitionsQuery.data,

    isLoadingBalance: customerBalanceQuery.isLoading,
    isLoadingTransactions: transactionHistoryQuery.isLoading,
    isLoadingTiers: tierDefinitionsQuery.isLoading,
    isLoadingGlobalSettings: globalSettingsQuery.isLoading,

    globalSettings: globalSettingsQuery.data,
    isUpdatingGlobalSettings: updateGlobalSettingsMutation.isPending,

    isEarningPoints: earnPointsMutation.isPending,
    isRedeemingPoints: redeemPointsMutation.isPending,
    isUpdatingTier: updateTierMutation.isPending,
    isCreatingTier: createTierMutation.isPending,
    isUpdatingTierDefinition: updateTierDefinitionMutation.isPending,
    isDeletingTierDefinition: deleteTierDefinitionMutation.isPending,

    error,

    selectCustomer,
    earnPoints: earnPointsMutation.mutateAsync,
    redeemPoints: redeemPointsMutation.mutateAsync,
    updateTier: updateTierMutation.mutateAsync,
    createTier: createTierMutation.mutateAsync,
    updateTierDefinition: updateTierDefinitionMutation.mutateAsync,
    deleteTierDefinition: deleteTierDefinitionMutation.mutateAsync,
    updateGlobalSettings: updateGlobalSettingsMutation.mutateAsync,
    loadMoreTransactions,
    clearError,
  };
};
